import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * kalshi.com's DS3 Button, in widget-app's spacing scale: a pill in Kalshi Sans medium that fades
 * on hover. `default` is DS3's primary, `outline` its secondary, `ghost` its tertiary; sizes are
 * DS3's md (44px) and sm (36px).
 */
const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-x50 border border-solid border-transparent font-kalshi-sans font-medium transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-x20 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-30',
    {
        variants: {
            variant: {
                default: 'border-0 bg-container-x10 text-core-inverse',
                secondary: 'bg-container-x30 text-text-x10',
                ghost: 'border-0 bg-transparent text-text-x10',
                outline: 'border-stroke-x30 bg-transparent text-text-x10',
                link: 'border-0 text-green-x10 underline-offset-4 hover:underline hover:opacity-100',
            },
            size: {
                default: 'min-h-11 gap-1.5 px-4 text-[15px] leading-[22px]',
                sm: 'min-h-9 gap-1.5 px-4 text-[13px] leading-[18px]',
                lg: 'min-h-12 gap-1.5 px-6 text-[15px] leading-[22px]',
                icon: 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
